import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BadgeCheck, Calendar, Crown, Heart, MessageCircle, Sparkles, Star, Store as StoreIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AdCard } from "@/components/ad-card";
import { UserAvatar } from "@/components/user-avatar";
import { VerifiedBadge } from "@/components/verified-badge";
import { useAuth } from "@/hooks/use-auth";
import { idFromSlug, SITE_URL } from "@/lib/seo";
import { isUuid } from "@/lib/listings";
import { azError } from "@/lib/az-errors";
import { isOnline } from "@/lib/presence";
import {
  fetchFollowStats,
  fetchPublicProfile,
  fetchUserListings,
  fetchUserRating,
  fetchUserStore,
  profileName,
  toggleFollowUser,
} from "@/lib/profiles";
import { storeSlug } from "@/lib/stores";

export const Route = createFileRoute("/istifadeci/$id")({
  head: ({ params }) => {
    const title = "İstifadəçi profili — Albazar.az";
    const url = `${SITE_URL}/istifadeci/${params.id}`;
    return {
      meta: [
        { title },
        {
          name: "description",
          content: "Albazar.az istifadəçisinin profili: aktiv elanlar, VIP və Premium elanlar, reytinq və əlaqə.",
        },
        { property: "og:title", content: title },
        { property: "og:description", content: "İstifadəçinin bütün elanlarına bax və birbaşa əlaqə saxla." },
        { property: "og:type", content: "profile" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: UserProfilePage,
});

function UserProfilePage() {
  const { id: slug } = Route.useParams();
  const userId = idFromSlug(slug);
  const { user } = useAuth();
  const valid = isUuid(userId);

  const qc = useQueryClient();

  const { data: profile, isLoading } = useQuery({
    queryKey: ["public-profile", userId],
    queryFn: () => fetchPublicProfile(userId),
    enabled: valid,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
  const { data: follow } = useQuery({
    queryKey: ["public-profile-follow", userId, user?.id ?? null],
    queryFn: () => fetchFollowStats(userId, user?.id ?? null),
    enabled: valid,
  });
  const followMutation = useMutation({
    mutationFn: (next: boolean) => toggleFollowUser(userId, user!.id, next),
    onSuccess: (_d, next) => {
      toast.success(next ? "İzləməyə başladınız" : "İzləmə dayandırıldı");
      void qc.invalidateQueries({ queryKey: ["public-profile-follow", userId] });
    },
    onError: (e) => toast.error(azError(e)),
  });
  const { data: listings = [] } = useQuery({
    queryKey: ["public-profile-listings", userId],
    queryFn: () => fetchUserListings(userId),
    enabled: valid,
  });
  const { data: rating } = useQuery({
    queryKey: ["public-profile-rating", userId],
    queryFn: () => fetchUserRating(userId),
    enabled: valid,
  });
  const { data: store } = useQuery({
    queryKey: ["public-profile-store", userId],
    queryFn: () => fetchUserStore(userId),
    enabled: valid,
  });

  if (!valid || (!isLoading && !profile)) {
    return (
      <div className="container-x py-24 text-center">
        <h1 className="text-2xl font-extrabold">İstifadəçi tapılmadı</h1>
        <Link to="/elanlar" search={{ q: "", kateqoriya: "", seher: "", sirala: "yeni" }}>
          <Button className="mt-5 rounded-full">Bütün elanlar</Button>
        </Link>
      </div>
    );
  }

  const name = profileName(profile ?? null);
  const since = profile?.created_at ? new Date(profile.created_at) : null;
  const vip = listings.filter((a) => a.badge === "vip");
  const premium = listings.filter((a) => a.badge === "premium");
  const isSelf = user?.id === userId;
  const online = isOnline(profile?.last_seen_at);

  return (
    <div className="container-x pb-16 pt-4">
      <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-card">
        {profile?.banner_url ? (
          <img
            src={profile.banner_url}
            alt={`${name} profil banneri`}
            className="h-32 w-full object-cover sm:h-44"
            loading="eager"
          />
        ) : (
          <div className="h-32 w-full bg-gradient-to-r from-primary/25 via-info/20 to-accent/25 sm:h-44" />
        )}
        <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-end">
          <UserAvatar
            src={profile?.avatar_url}
            name={name}
            online={online}
            className="-mt-14 size-24 border-4 border-card shadow-elevated"
          />
          <div className="min-w-0 flex-1">
            <h1 className="flex items-center gap-2 truncate text-2xl font-extrabold">
              {name}
              <VerifiedBadge verified={profile?.verified} compact />
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-semibold ${
                  online ? "bg-green-500/15 text-green-600" : "bg-muted text-muted-foreground"
                }`}
              >
                <span className={`size-2 rounded-full ${online ? "bg-green-500" : "bg-gray-400"}`} />
                {online ? "Online" : "Offline"}
              </span>
              {follow && follow.count > 0 && (
                <span className="inline-flex items-center gap-1">
                  <Heart className="size-3.5" /> {follow.count} izləyici
                </span>
              )}
              {since && (
                <span className="inline-flex items-center gap-1">
                  <Calendar className="size-3.5" />
                  {since.toLocaleDateString("az-AZ", { month: "long", year: "numeric" })}-dan üzv
                </span>
              )}
              {rating && rating.count > 0 && (
                <span className="inline-flex items-center gap-1 font-bold text-foreground">
                  <Star className="size-3.5 fill-vip text-vip" />
                  {rating.average.toFixed(1)} ({rating.count} rəy)
                </span>
              )}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {store?.verified && (
                <Badge className="rounded-full bg-info text-background">
                  <BadgeCheck className="mr-1 size-3.5" /> Təsdiqlənmiş
                </Badge>
              )}
              {store && (
                <Link to="/magaza/$slug" params={{ slug: storeSlug(store as never) }}>
                  <Badge variant="outline" className="rounded-full">
                    <StoreIcon className="mr-1 size-3.5" /> {store.name}
                  </Badge>
                </Link>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {isSelf ? (
              <Link to="/profil">
                <Button className="h-12 rounded-2xl px-6 text-base">Profilim</Button>
              </Link>
            ) : (
              <>
                <Button
                  variant={follow?.following ? "outline" : "secondary"}
                  className="h-12 rounded-2xl px-5 text-base"
                  disabled={followMutation.isPending}
                  onClick={() => {
                    if (!user?.id) {
                      toast.error("Əvvəlcə hesabınıza daxil olun");
                      return;
                    }
                    followMutation.mutate(!follow?.following);
                  }}
                >
                  <Heart
                    className={`size-4 ${follow?.following ? "fill-red-500 text-red-500" : ""}`}
                  />
                  {follow?.following ? "İzlənilir" : "Takib et"}
                </Button>
                <Link to="/mesajlar" search={{ user: userId }}>
                  <Button className="h-12 rounded-2xl px-6 text-base shadow-glow">
                    <MessageCircle className="size-4" /> Mesaj yaz
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3">
        <Stat label="Aktiv elan" value={listings.length} />
        <Stat label="VIP" value={vip.length} icon={<Crown className="size-3.5 text-vip" />} />
        <Stat
          label="Premium"
          value={premium.length}
          icon={<Sparkles className="size-3.5 text-premium" />}
        />
      </div>

      <Tabs defaultValue="all" className="mt-6">
        <TabsList className="rounded-full">
          <TabsTrigger value="all" className="rounded-full">Bütün elanlar</TabsTrigger>
          <TabsTrigger value="vip" className="rounded-full">VIP</TabsTrigger>
          <TabsTrigger value="premium" className="rounded-full">Premium</TabsTrigger>
        </TabsList>
        {(
          [
            ["all", listings],
            ["vip", vip],
            ["premium", premium],
          ] as const
        ).map(([key, items]) => (
          <TabsContent key={key} value={key} className="mt-5">
            {items.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
                Bu bölmədə elan yoxdur.
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {items.map((a, i) => (
                  <AdCard key={a.id} ad={a} index={i} />
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function Stat({ label, value, icon }: { label: string; value: number; icon?: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4 text-center shadow-card">
      <p className="text-2xl font-black">{value}</p>
      <p className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground">
        {icon} {label}
      </p>
    </div>
  );
}
